import { FormProvider } from 'react-hook-form'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/view/components/ui/dialog'
import { BasicInput } from '@/view/components/BasicInput'
import { BasicSelect } from '@/view/components/BasicSelect'
import { Button } from '@/view/components/ui/button'
import { Label } from '@/view/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/view/components/ui/radio-group'
import { cpfMask } from '@/app/utils/cpfFormater'
import useGenerateProposalModal from './useGenerateProposalModal'
import { phoneMask } from '@/app/utils/phoneFormater'
import { dateMask } from '@/app/utils/dateFormater'
import { moneyMask } from '@/app/utils/formatMoneyValue'
import { PartnerType } from '@/app/utils/partners.enum'
import { StepProgress } from '../components/StepProgress'

interface GenerateProposalModalProps {
  onContinue: () => void
  onContinuePreApproved: () => void
  onBack: () => void
  partnerType?: PartnerType.CREFISA | PartnerType.CREFISA_INSS
}

function YesNoQuestion({
  label,
  value,
  onChange,
}: {
  label: string
  value?: string
  onChange: (value: string) => void
}) {
  return (
    <div>
      <Label className="text-xs font-medium leading-snug md:text-sm">
        {label}
      </Label>
      <RadioGroup
        value={value}
        onValueChange={onChange}
        className="mt-2 flex gap-6"
      >
        <Label className="flex items-center gap-2 text-sm font-medium">
          <RadioGroupItem value="sim" />
          Sim
        </Label>
        <Label className="flex items-center gap-2 text-sm font-medium">
          <RadioGroupItem value="nao" />
          Não
        </Label>
      </RadioGroup>
    </div>
  )
}

export default function GenerateProposalModal({
  onContinue,
  onContinuePreApproved,
  onBack,
  partnerType = PartnerType.CREFISA,
}: GenerateProposalModalProps) {
  const {
    form,
    submitModal,
    loading,
    isInss,
    isUnderage,
    receivedDateOptions,
    steps,
  } = useGenerateProposalModal({
    partnerType,
    onContinue,
    onContinuePreApproved,
  })

  const {
    watch,
    setValue,
    formState: { errors },
  } = form

  const possuiEmprestimo = watch('possuiEmprestimo')
  const possuiDebitos = watch('possuiDebitos')
  const recebeViaCrefisa = watch('recebeViaCrefisa')

  return (
    <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-[400px] flex-col overflow-y-auto md:max-w-[400px]">
      <DialogHeader className="border-b pb-4 pr-16 text-left md:pr-0">
        <DialogTitle className="text-lg font-semibold text-black01 md:text-xl">
          Vamos simular seu crédito
        </DialogTitle>
        <DialogDescription className="!mt-0 text-sm text-black04 md:text-base">
          Preencha o cpf abaixo para gerar os convênios disponíveis para
          simulação
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submitModal}>
        <FormProvider {...form}>
          <div className="flex flex-col gap-4">
            {loading && steps.length > 0 && (
              <StepProgress steps={steps} title="Consultando o cliente" />
            )}
            <BasicInput
              name="cpf"
              maxLength={14}
              label="CPF"
              error={errors.cpf?.message}
              onInput={cpfMask}
            />

            <BasicInput
              name="phone"
              label="Telefone"
              error={errors.phone?.message}
              onInput={phoneMask}
            />

            {isInss && (
              <BasicInput
                name="birthDate"
                label="Data de nascimento"
                placeholder="dd/mm/aaaa"
                maxLength={10}
                inputMode="numeric"
                error={errors.birthDate?.message}
                onInput={dateMask}
              />
            )}

            <BasicSelect
              name="codeReceiptDataId"
              label="Data de pagamento"
              placeholder="Selecione a data de pagamento"
              options={receivedDateOptions}
              error={errors.codeReceiptDataId?.message}
            />

            {isInss && (
              <>

                <BasicInput
                  name="valorBeneficio"
                  label="Valor do benefício"
                  placeholder="R$ 0,00"
                  inputMode="numeric"
                  error={errors.valorBeneficio?.message}
                  onInput={moneyMask}
                />

                <YesNoQuestion
                  label="Cliente possui empréstimo?"
                  value={possuiEmprestimo}
                  onChange={(value) => {
                    setValue('possuiEmprestimo', value, {
                      shouldValidate: true,
                    })
                    if (value === 'nao') setValue('valorEmprestimoAtivo', '')
                  }}
                />

                {possuiEmprestimo === 'sim' && (
                  <BasicInput
                    name="valorEmprestimoAtivo"
                    label="Valor do empréstimo ativo"
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                    error={errors.valorEmprestimoAtivo?.message}
                    onInput={moneyMask}
                  />
                )}

                <YesNoQuestion
                  label="Cliente recebe via Crefisa?"
                  value={recebeViaCrefisa}
                  onChange={(value) =>
                    setValue('recebeViaCrefisa', value, {
                      shouldValidate: true,
                    })
                  }
                />

                <YesNoQuestion
                  label="Existem débitos automáticos ou agendados no dia do recebimento do benefício?"
                  value={possuiDebitos}
                  onChange={(value) => {
                    setValue('possuiDebitos', value, { shouldValidate: true })
                    if (value === 'nao') setValue('valorTotalDebitos', '')
                  }}
                />

                {possuiDebitos === 'sim' && (
                  <BasicInput
                    name="valorTotalDebitos"
                    label="Valor total dos débitos"
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                    error={errors.valorTotalDebitos?.message}
                    onInput={moneyMask}
                  />
                )}
              </>
            )}
          </div>

          <div className="mt-8 flex items-center justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="w-full max-w-[160px] flex-1 rounded-full text-primary"
            >
              Voltar
            </Button>
            <Button
              type="submit"
              className="w-full max-w-[160px] flex-1 rounded-full font-semibold text-white"
              isLoading={loading}
              disabled={loading || isUnderage}
            >
              Prosseguir
            </Button>
          </div>
        </FormProvider>
      </form>
    </DialogContent>
  )
}
